import { useEffect, useState, useCallback, useRef } from 'react';
import { debounce, merge, forEach, find, defaultTo } from 'lodash';
import { ecommerceToast, toast } from '../../../util/toasts';
import { useGooglePlacesAPI } from '../../../clients/google-api/google-places-client';

// Google Address required fields to store in the database (from Google Place Details)
export interface GoogleAddress {
  address1: string;
  postcode: string;
  city: string;
  state: string;
  country: string;
}

// Google Place Prediction type when user types in the input
export interface PlacePrediction {
  placeId: string;
  text?: {
    text: string;
  };
  structuredFormat?: {
    mainText: {
      text: string;
    };
    secondaryText?: {
      text: string;
    };
  };
}

// Google Place Details type when user selects a prediction
export interface PlaceDetails {
  formattedAddress: string;
  addressComponents: Array<{
    longText: string;
    shortText: string;
    types: string[];
  }>;
}

// Props for the usePlacesAutocomplete hook
export interface UsePlacesAutocompleteProps {
  onChange?: (value: string) => void;
  onPredictionSelected?: (address: GoogleAddress) => void;
  value?: string;
  apiType?: 'customer' | 'admin';
  inputRef?: React.RefObject<HTMLInputElement | HTMLDivElement>;
}

export const usePlacesAutocomplete = ({
  onChange,
  onPredictionSelected,
  value,
  apiType = 'admin',
  inputRef,
}: UsePlacesAutocompleteProps) => {
  const { placesAutocomplete, getPlaceDetails } = useGooglePlacesAPI(apiType);

  const [inputText, setInputText] = useState(value || '');
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isPredictionsLoading, setIsPredictionsLoading] = useState(false);
  const [isPlaceDetailsLoading, setIsPlaceDetailsLoading] = useState(false);

  // Ref to track blur timeout
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize inputText with external value
  useEffect(() => {
    setInputText(defaultTo(value, ''));
  }, []);

  // Show error toast on failed request
  const showErrorToast = useCallback(
    (error: string) => {
      apiType === 'admin' ? toast(error, 'error') : ecommerceToast(error, 'error');
    },
    [apiType],
  );

  // Fetch predictions from Google Places API
  const fetchPredictions = useCallback(
    async (input: string) => {
      // If input is less than 2 characters, return
      if (input.length < 2) {
        setPredictions([]);
        setIsOpen(false);
        return;
      }

      // Set loading state
      setIsPredictionsLoading(true);

      try {
        const data = await placesAutocomplete(input);

        // Handle empty or invalid response
        if (!data || !data.suggestions) {
          setPredictions([]);
          setIsOpen(false);
          return;
        }

        // Set predictions with safe mapping
        const predictions = defaultTo(data.suggestions, [])
          .map((suggestion) => suggestion?.placePrediction)
          .filter((prediction): prediction is PlacePrediction => prediction != null); // Type guard to narrow the type

        setPredictions(predictions);

        // Set open state
        setIsOpen(predictions.length > 0);
        setSelectedIndex(-1);
      } catch (error) {
        showErrorToast('Error getting predictions');
        setPredictions([]);
        setIsOpen(false);
      } finally {
        setIsPredictionsLoading(false);
      }
    },
    [placesAutocomplete],
  );

  // Handle input changes with debouncing
  const debouncedFetchPredictions = useCallback(
    debounce((value: string) => {
      fetchPredictions(value);
    }, 300),
    [fetchPredictions],
  );

  const handleInputChange = useCallback(
    (value: string) => {
      setInputText(value);
      onChange?.(value); // Call external onChange
      debouncedFetchPredictions(value);
    },
    [debouncedFetchPredictions, onChange],
  );

  // Parse address components with the highest priority type for each required field of GoogleAddress
  const parseAddressComponents = useCallback((components: any[]) => {
    const addressData: Partial<GoogleAddress> = {};

    // Priority order for each field
    const fieldPriorities = {
      postcode: ['postal_code', 'postal_code_suffix'],
      city: ['locality'],
      state: ['administrative_area_level_1'],
      country: ['country'],
    };

    forEach(components, (component) => {
      if (!component.types || !component.longText) return;

      // Find the highest priority type for this component
      for (const [field, priorityTypes] of Object.entries(fieldPriorities)) {
        const matchingType = find(component.types, (type: string) => priorityTypes.includes(type));

        if (matchingType) {
          // Handle special cases
          if (field === 'postcode' && addressData.postcode) {
            // Combine postal code parts
            if (matchingType === 'postal_code_suffix') {
              addressData.postcode = `${addressData.postcode}-${component.longText}`;
            } else {
              addressData.postcode = `${component.longText}${addressData.postcode}`;
            }
          } else {
            // Set the field value
            addressData[field as keyof GoogleAddress] = component.longText;
          }
          break;
        }
      }
    });

    return addressData;
  }, []);

  // Handle prediction selection
  const handlePredictionClick = async (prediction: PlacePrediction) => {
    // Prevent multiple simultaneous calls
    if (isPlaceDetailsLoading) {
      return;
    }

    // Cancel any pending blur timeout to prevent race conditions
    cancelBlurTimeout();

    // Set loading state
    setIsPlaceDetailsLoading(true);

    try {
      // Get place details using the new Places API
      const response = await getPlaceDetails(prediction.placeId);

      // Validate response
      if (!response || !response.formattedAddress) {
        throw new Error('Invalid place details response');
      }

      const placeDetails = response;

      // Set input text
      const formattedAddress = defaultTo(placeDetails.formattedAddress, '');
      setInputText(formattedAddress);
      onChange?.(formattedAddress);

      // If onPredictionSelected is provided, set the address object
      if (onPredictionSelected) {
        // Set address object
        const address: GoogleAddress = {
          address1: defaultTo(placeDetails.formattedAddress, ''),
          postcode: '',
          city: '',
          state: '',
          country: '',
        };

        // Parse address components
        if (placeDetails.addressComponents) {
          const parsedAddress = parseAddressComponents(placeDetails.addressComponents);
          merge(address, parsedAddress);
        }

        onPredictionSelected(address);
      }

      // Clear predictions and close dropdown
      setPredictions([]);
      setIsOpen(false);
      setSelectedIndex(-1);
    } catch (error) {
      showErrorToast('Error getting place details');

      // Even on error, clear the dropdown to prevent stuck state
      setPredictions([]);
      setIsOpen(false);
      setSelectedIndex(-1);
    } finally {
      setIsPlaceDetailsLoading(false);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev < predictions.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && predictions[selectedIndex]) {
            handlePredictionClick(predictions[selectedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setSelectedIndex(-1);
          break;
      }
    },
    [isOpen, predictions, selectedIndex, handlePredictionClick],
  );

  // Handle focus events
  const handleFocus = useCallback(() => {
    if (predictions.length > 0) {
      setIsOpen(true);
    }
  }, [predictions.length]);

  // Handle blur events
  const handleBlur = useCallback(() => {
    // Clear any existing timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    blurTimeoutRef.current = setTimeout(() => setIsOpen(false), 200);
  }, []);

  // Cancel blur timeout (called when prediction is clicked)
  const cancelBlurTimeout = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  }, []);

  // Setup input element event listeners and autofill protection
  useEffect(() => {
    if (!inputRef?.current) return;

    // Handle both cases: direct input element or wrapper div
    const inputElement =
      inputRef.current instanceof HTMLInputElement
        ? inputRef.current
        : (inputRef.current.querySelector('input') as HTMLInputElement);

    if (!inputElement) return;

    // Prevent autofill
    inputElement.setAttribute('autocomplete', 'off');
    inputElement.setAttribute('data-1p-ignore', '');
    inputElement.setAttribute('data-lpignore', 'true');
    inputElement.setAttribute('data-form-type', 'other');

    // Add event listeners
    inputElement.addEventListener('keydown', handleKeyDown);
    inputElement.addEventListener('focus', handleFocus);
    inputElement.addEventListener('blur', handleBlur);

    return () => {
      inputElement.removeEventListener('keydown', handleKeyDown);
      inputElement.removeEventListener('focus', handleFocus);
      inputElement.removeEventListener('blur', handleBlur);
    };
  }, [inputRef, handleKeyDown, handleFocus, handleBlur]);

  // Cleanup blur timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  return {
    // State
    inputText,
    predictions,
    isOpen,
    selectedIndex,
    isPredictionsLoading,
    isPlaceDetailsLoading,

    // Actions
    handleInputChange,
    handlePredictionClick,

    // Setters
    setIsOpen,
    setSelectedIndex,
  };
};
